import * as React from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { Tabs } from '@independent-software/typeui/controls/Tabs';
import { ToastService } from '@independent-software/typeui/controls/Toast';
import { Flex } from '@independent-software/typeui/controls/Flex';
import { Header } from '@independent-software/typeui/controls/Header';
import { Datum } from '@independent-software/typeui/formatters/Datum';
import { Markdown } from '@independent-software/typeui/formatters/Markdown';
import { Label } from '@independent-software/typeui/controls/Label';

import { AuthContext } from '../../AuthContext';
import { FilterContext } from '../../FilterContext';
import { Section, Viewer } from '../../modules';
import { Campaign, CampaignFactory, Sample } from '../../resource/'; 
import { Output } from '../../controls/Output';
import { TidalSituation } from './TidalSituation';
import { ListSamples } from '../Sample';

const ViewCampaign = () => {
  const auth = React.useContext(AuthContext);
  const filter = React.useContext(FilterContext);
  const navigate = useNavigate();
  const { id } = useParams();

  const handleEdit = () => {
    navigate(`/campaigns/edit/${parseInt(id)}`);
  }

  const handleDelete = () => {
    ToastService.toast("Campaign deleted.");
    navigate('/campaigns');
  }    

  const handleSampleClick = (item:Sample) => {
    navigate(`/samples/${item.id}`);
  }  

  return (
    <Viewer<Campaign> 
      auth={auth}
      id={parseInt(id)}
      factory={CampaignFactory}
      canEdit='can_edit_campaigns'
      canDelete='can_delete_campaigns'
      onEdit={handleEdit}
      onDelete={handleDelete}
      content={(campaign: Campaign) => 
        <React.Fragment>
          <Section dark padded>
            <Header size="h2">{campaign.name}</Header>
          </Section>
          <Tabs underlined>
            <Tabs.Pane label="General">
              <Section padded="very">
                <Flex.Columns count={2}>
                  <>
                    <Output label="Name" hint="Campaign name">{campaign.name}</Output>
                    <Output label="Date" hint="Campaign date"><Datum.LongDate value={campaign.date}/></Output>
                    <Output label="Project" hint="Project">{campaign.project ? campaign.project.name : '-'}</Output>
                  </>
                  <Output label="Description" hint="Campaign description"><Markdown source={campaign.description}/></Output>
                </Flex.Columns>
              </Section>
            </Tabs.Pane>
            <Tabs.Pane label="Hydrology">
              <Section padded="very">
                <Flex.Columns count={2}>
                  <Output label="Freshwater discharge" hint="River flow into system" unit="m&sup3;/s">{campaign.freshwater_discharge ? campaign.freshwater_discharge : '-'}</Output>
                  <></>
                  <Output label="Tidal amplitude" hint="Maximum water height above mid-tide mark" unit="m">{campaign.tidal_amplitude ? campaign.tidal_amplitude : "-"}</Output>
                  <Output label="Tidal situation" hint="Tide condition">{TidalSituation.toName(campaign.tidal_situation)}</Output>
                  <Output label="High water time" hint="Time of high tide">{campaign.high_time ? <Datum.Time value={campaign.high_time}/> : '-'}</Output>
                  <Output label="Low water time" hint="Time of low tide">{campaign.low_time ? <Datum.Time value={campaign.low_time}/> : '-'}</Output>
                </Flex.Columns>
              </Section>
            </Tabs.Pane>
            <Tabs.Pane label="Weather">
              <Section padded="very">
                <Flex.Columns count={2}>
                  <Output label="Solar radiation" hint="Solar radiation (maximum)" unit="W/m&sup2;">{campaign.radiation ? campaign.radiation : '-'}</Output>
                  <Output label="Rainfall" hint="Cumulative rainfall for the previous five days" unit="mm">{campaign.rainfall ? campaign.rainfall : '-'}</Output>
                  <Output label="Air temperature (minimum)" hint="Lowest registered air temperature" unit="&ordm;C">{campaign.low_temperature ? campaign.low_temperature : '-'}</Output>
                  <Output label="Air temperature (maximum)" hint="Highest registered air temperature" unit="&ordm;C">{campaign.high_temperature ? campaign.high_temperature : '-'}</Output>
                  <Output label="Wind direction" hint="Wind direction in degrees (north is 360)" unit="deg">{campaign.wind_direction ? campaign.wind_direction : '-'}</Output>
                  <Output label="Wind speed" hint="Wind speed in meters per second" unit={<>m/s</>}>{campaign.wind_speed ? campaign.wind_speed : '-'}</Output>
                  <Output label="Weather" hint="Weather conditions (e.g. slightly overcast, warm, light breeze)">{campaign.weather}</Output>
                </Flex.Columns>
              </Section>
            </Tabs.Pane>
            <Tabs.Pane label={<span>Samples <Label size="small">{campaign.samples_count}</Label></span>}>
              <ListSamples auth={auth} filter={filter} name="campaign_samples" campaignId={campaign.id} onClick={handleSampleClick}/>
            </Tabs.Pane>                 
          </Tabs>
        </React.Fragment>}>
    </Viewer>
  );
}

export { ViewCampaign }
